import React, { useEffect } from "react";
import styled from "styled-components";

import { SideBar } from "components/nav/SideBar";
import { Content, MainContainer } from "styles/styles";
import { useNavigationStore } from "store/navigationStore";
import { useRealtimeStt } from "hooks/useRealtimeStt";
import { useRealtimeSttStore } from "store/realtimeSttStore";

import MicVisualizer from "features/results/MicVisualizer";
import IntroContainer from "features/upload/IntroContainer";
import RealtimeSttPanel from "features/results/ContentSection/Panels/RealtimeSttPanel";
import { designTokens, getTypography } from "@/styles/designTokens";
import {
  FlowBody,
  FlowLayoutContainer,
  ScriptContainer,
} from "../VoiceUnderstandingPage";


const RealtimeSttPage: React.FC = () => {
  const { isCollapsed } = useNavigationStore();
  const { isRecording, volume, startRecording, stopRecording, pauseValue, setPauseValue, endValue, setEndValue } =
    useRealtimeStt();
  const { elapsedTime, resetTranscripts } = useRealtimeSttStore();

  useEffect(() => () => resetTranscripts(), [resetTranscripts]);

  // 일반 모드를 기본값으로 설정
  useEffect(() => {
    setPauseValue(0.1);
    setEndValue(1.0);
  }, [setPauseValue, setEndValue]);

  const handlePauseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = parseFloat(e.target.value);
    setPauseValue(newVal);
    if (newVal >= endValue) setEndValue(Math.min(10, newVal + 0.1));
  };
  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = parseFloat(e.target.value);
    if (newVal > pauseValue) setEndValue(newVal);
  };

  return (
    <MainContainer>
      <Content $isCollapsed={isCollapsed}>
        <SideBar />
        <FlowLayoutContainer>
          <IntroSection>
            <IntroContainer
              headline="실시간 음성인식"
              description={{
                text: "말하는 순간, 텍스트가 완성됩니다.\n",
                highlight: "회의, 상담, 챗봇 ",
                text2: "등 다양한 실시간 서비스에 활용해보세요.",
              }}
            />
          </IntroSection>
          <FlowBody>
            <LeftSection>
              <VisualizerSection>
                <MicVisualizer
                  volume={volume}
                  isRecording={isRecording}
                  onToggle={() =>
                    isRecording ? stopRecording() : startRecording()
                  }
                />
                {isRecording && <Timer>{formatTime(elapsedTime)}</Timer>}
              </VisualizerSection>

              <SliderPanel>
                <ModeSection>
                  <Label>인식 모드</Label>
                  <ButtonSection>
                    <PresetButton
                      $isActive={pauseValue === 0.1 && endValue === 1.0}
                      onClick={() => {
                        setPauseValue(0.1);
                        setEndValue(1.0);
                      }}
                      disabled={isRecording}
                    >
                      <ButtonTooltip>
                        <TooltipText>일상 대화</TooltipText>
                      </ButtonTooltip>
                      일반
                    </PresetButton>

                    <PresetButton
                      $isActive={pauseValue === 0.1 && endValue === 3.0}
                      onClick={() => {
                        setPauseValue(0.1);
                        setEndValue(3.0);
                      }}
                      disabled={isRecording}
                    >
                      <ButtonTooltip>
                        <TooltipText>느린 발음 인식에 최적화</TooltipText>
                      </ButtonTooltip>
                      천천히
                    </PresetButton>
                  </ButtonSection>
                </ModeSection>
                <Row>
                  <Label>Pause</Label>
                  <RangeInput
                    type="range"
                    min={0.1}
                    max={5.0}
                    step={0.1}
                    value={pauseValue}
                    onChange={handlePauseChange}
                    disabled={isRecording}
                  />
                  <Value>{pauseValue.toFixed(1)}s</Value>
                </Row>

                <Row>
                  <Label>End</Label>
                  <RangeInput
                    type="range"
                    min={0.1}
                    max={10.0}
                    step={0.1}
                    value={endValue}
                    onChange={handleEndChange}
                    disabled={isRecording}
                  />
                  <Value>{endValue.toFixed(1)}s</Value>
                </Row>
                <Notice>* Pause 값은 End 값보다 작아야 합니다.</Notice>
              </SliderPanel>
            </LeftSection>

            <ScriptContainer style={{ padding: "32px 24px 32px 32px" }}>
              <RealtimeSttPanel />
            </ScriptContainer>
          </FlowBody>
        </FlowLayoutContainer>
      </Content>
    </MainContainer>
  );
};

export default RealtimeSttPage;

/* ----------------------------- styled-components ----------------------------- */

const IntroSection = styled.div`
  padding: 48px 56px 0 56px;
`;

const VisualizerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  flex: 1;
  min-height: 200px;
  justify-content: center;
`;

const Timer = styled.div`
  ${getTypography("korean", "body2")}
  color: ${designTokens.colors.font.primary};
  font-weight: 600;
`;

const SliderPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  background: white;
  padding: 24px 32px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);
  max-width: 520px;
  align-self: center;
  flex-shrink: 0;
  margin: 0 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Label = styled.span`
  min-width: 60px;
  ${getTypography("korean", "body3")}
  font-weight: 600;
`;

const RangeInput = styled.input`
  flex: 1;
  -webkit-appearance: none;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    background: #4b5eff;
    border-radius: 50%;
    cursor: pointer;
  }
`;

const Value = styled.span`
  min-width: 45px;
  ${getTypography("korean", "body3")}
  text-align: right;
`;

const Notice = styled.div`
  ${getTypography("korean", "body4")}
  color: ${designTokens.colors.font.quaternary};
  text-align: center;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 360px;
  height: 100%;
  min-height: 0;
  overflow: visible;
  gap: 16px;
  padding: 32px 24px 32px 32px;
`;

const ModeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ButtonSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const ButtonTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin: 0 auto;
  transform: translateY(-8px);
  background: #1f2937;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  ${getTypography("korean", "body5")}
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 10;
  width: fit-content;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #1f2937;
  }
`;

const PresetButton = styled.button<{ $isActive: boolean }>`
  position: relative;
  flex: 1;
  padding: 8px 16px;
  border: 2px solid ${({ $isActive }) => ($isActive ? "#4b5eff" : "#e5e7eb")};
  border-radius: 8px;
  background: ${({ $isActive }) => ($isActive ? "#4b5eff" : "white")};
  color: ${({ $isActive }) => ($isActive ? "white" : "#374151")};
  ${getTypography("korean", "body3")}
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #4b5eff;
    background: ${({ $isActive }) => ($isActive ? "#4b5eff" : "#f8faff")};
    color: ${({ $isActive }) => ($isActive ? "white" : "#4b5eff")};
    
    ${ButtonTooltip} {
      opacity: 1;
      visibility: visible;
      transform: translateY(-8px);
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    
    &:hover {
      border-color: #e5e7eb;
      background: white;
      color: #374151;
      
      ${ButtonTooltip} {
        opacity: 0;
        visibility: hidden;
      }
    }
  }
`;

const TooltipText = styled.span`
  display: block;
`;

const formatTime = (s: number) => {
  const m = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const sec = (s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};
